import type { Source } from "@types"

/**
 * Variables d’alarme « composées » au niveau bâtiment : pas de clé dédiée dans
 * `monitoring.firstdate` ; on s’appuie sur les compteurs et des variables sous-jacentes.
 */
const BUILDING_COMPOSITE_ALARM_VARIABLES = {
    energiepardju_elec_tout: ["energie_elec_tout"] as const,
    energiepardju_gaz_tout: ["energie_gaz_tout"] as const,
    energie_tout_tout: [
        "energie_elec_tout",
        "energie_gaz_tout",
        "energie_chaleur_tout",
    ] as const,
} as const

type BuildingCompositeKey = keyof typeof BUILDING_COMPOSITE_ALARM_VARIABLES

function isBuildingCompositeVariable(
    variableType: string
): variableType is BuildingCompositeKey {
    return variableType in BUILDING_COMPOSITE_ALARM_VARIABLES
}

/**
 * Indique si une entrée existe dans `monitoring.firstdate` pour une variable
 * (au moins une fréquence renseignée).
 *
 * @param monitoring bloc monitoring de la source
 * @param variableKey identifiant de variable (ex. `energie_elec_tout`)
 */
export function hasMonitoringFirstDateEntry(
    monitoring: Source["monitoring"] | undefined,
    variableKey: string
): boolean {
    const entry = monitoring?.firstdate?.[variableKey]
    if (!entry || typeof entry !== "object") return false
    return Object.values(entry).some(
        (v) => v != null && String(v).trim() !== ""
    )
}

/** Identifiant d’opérateur des compteurs Enedis (`Source.operator`). */
const ENEDIS_OPERATOR = "enedis"

/**
 * DEV-1506 / DEV-1421 — LISTE BLANCHE des chemins
 * `Source.monitoring.firstdate.<variable>.<fréquence>` dont l’écrivain est encore
 * VIVANT sur les sources Enedis (refonte enedis).
 *
 * ⚠️ MIROIR de `BORNES_VIVANTES_ENEDIS` côté dapi — repo `Homeys-io/homeys-api-data`,
 * fichier `modules/source/get_information_source.py`, PR #183 — lue au sha SERVI
 * `8dcc4de4` (= homeys-dapi-prod v209). Les deux listes doivent rester identiques :
 * toute extension ici doit être faite là-bas, et inversement, sinon elles divergent
 * en silence.
 *
 * Mesure du 26/08 au soir (prod app4, collection `Source`, `operator: "enedis"` —
 * 186 213 documents) : 144 chemins `variable.fréquence` distincts portent une valeur
 * non vide sous `monitoring.firstdate` (144 également sous `lastdate` ; union : 144).
 * SIX seulement sont dans cette liste — les 138 autres sont FIGÉS depuis le 16-17/08
 * (l’écrivain legacy `data/updateapp` ne tourne plus pour les pdl). Le dossier
 * « Le servi, vérifié » annonçait 147 fossiles sur 151 : même fait, périmètre de
 * comptage différent ; le chiffre retenu ici est celui recompté au correctif.
 * Une variable hors de cette liste n’a donc plus AUCUN écrivain : proposer une
 * alarme dessus arme une surveillance sur une donnée morte — l’alarme ne se
 * déclenchera jamais, ou se déclenchera sur une valeur qui ne bouge plus.
 *
 * `min_freq`/`max_freq`.rawperiode (bornes de facturation, épisodiques mais écrites
 * par la refonte) sont listées pour documenter le périmètre vivant complet ; elles
 * ne sont de toute façon pas des variables d’alarme (absentes de `ALARME_ALLOWED_*`).
 */
const ENEDIS_LIVE_FIRSTDATE_FREQUENCIES: Readonly<
    Record<string, readonly string[]>
> = {
    energie_elec_tout: ["daily", "raw"],
    energie_elecinjection_tout: ["daily", "raw"],
    min_freq: ["rawperiode"],
    max_freq: ["rawperiode"],
}

/**
 * Indique si une variable est encore ALIMENTÉE pour une source donnée.
 *
 * Opérateur ≠ `enedis` (grdf, cpcu, dalkia, regaz, fidelix, techem, sigfox,
 * objenious, orange, meteofrance, openmeteo, rte…) : comportement STRICTEMENT
 * inchangé — harvest ou `data/updateapp` écrivent encore leurs chemins, on se
 * contente de la présence d’une entrée `firstdate` (cf. `hasMonitoringFirstDateEntry`).
 *
 * Opérateur `enedis` : la variable ET la fréquence doivent figurer dans
 * `ENEDIS_LIVE_FIRSTDATE_FREQUENCIES`. Le test porte sur la fréquence, pas
 * seulement sur la variable : une `hourly` fossile sous une variable vivante est
 * écartée elle aussi (même règle que dapi, qui neutralise le grain mort).
 *
 * @param source source courante (on lit `operator` ET `monitoring`)
 * @param variableKey identifiant de variable (ex. `energie_elec_tout`)
 */
export function hasLiveMonitoringFirstDateEntry(
    source: Pick<Source, "monitoring" | "operator"> | undefined,
    variableKey: string
): boolean {
    if (!source) return false

    if (source.operator !== ENEDIS_OPERATOR) {
        return hasMonitoringFirstDateEntry(source.monitoring, variableKey)
    }

    const liveFrequencies = ENEDIS_LIVE_FIRSTDATE_FREQUENCIES[variableKey]
    if (!liveFrequencies) return false

    const entry = source.monitoring?.firstdate?.[variableKey]
    if (!entry || typeof entry !== "object") return false

    return Object.entries(entry).some(
        ([frequency, value]) =>
            liveFrequencies.includes(frequency) &&
            value != null &&
            String(value).trim() !== ""
    )
}

/**
 * Source : une variable d’alarme n’est proposée que si elle apparaît dans
 * `monitoring.firstdate` pour cette source — et, sur les sources Enedis, que si
 * son écrivain est encore vivant (DEV-1506).
 *
 * @param variableType type `ConfigVariable`
 * @param source source courante
 */
export function shouldIncludeAlarmVariableForSource(
    variableType: string,
    source: Source | undefined
): boolean {
    if (!source) return false
    return hasLiveMonitoringFirstDateEntry(source, variableType)
}

/**
 * Bâtiment : union des `firstdate` sur toutes les sources rattachées ; pour les
 * variables composées, uniquement les compteurs et les clés sous-jacentes définies ci-dessus.
 *
 * Le filtre Enedis (DEV-1506) s’applique SOURCE PAR SOURCE : un bâtiment qui mixe
 * un compteur Enedis et un capteur IoT garde toutes les options portées par le
 * capteur. Une option ne disparaît que si AUCUNE source du bâtiment ne l’alimente
 * encore.
 *
 * @param variableType type `ConfigVariable`
 * @param buildingSources toutes les sources du bâtiment (compteurs, IoT, météo, etc.)
 * @param meterSources sous-ensemble compteurs (types `METER_TYPES_LIST`)
 */
export function shouldIncludeAlarmVariableForBuilding(
    variableType: string,
    buildingSources: Source[],
    meterSources: Source[]
): boolean {
    if (isBuildingCompositeVariable(variableType)) {
        const keys = BUILDING_COMPOSITE_ALARM_VARIABLES[variableType]
        return meterSources.some((s) =>
            keys.some((key) => hasLiveMonitoringFirstDateEntry(s, key))
        )
    }

    return buildingSources.some((s) =>
        hasLiveMonitoringFirstDateEntry(s, variableType)
    )
}
